import { api } from '../api.js';
import { createPaginationNavigationGuard } from '../pagination-navigation.js';
import { navigate } from '../router.js';
import { GAME_REPORT_REASON_LABELS } from '../../../../src/shared/game-report-reasons.js';
import { esc } from '../../../../src/shared/escape-html.js';
import { formatDateTime as fmtDate } from '../../../../src/shared/format-time.js';

function statusBadge(status) {
  return `<span class="contact-status-${status}">${status}</span>`;
}

function typeBadge(r) {
  if (r.type === 'game_report') {
    const reason = GAME_REPORT_REASON_LABELS[r.reason] || r.reason || 'Report';
    return `<span class="contact-status-new" title="${esc(reason)}">🚩 Game</span>`;
  }
  return '<span style="opacity:0.6">Contact</span>';
}

let _listBound = false;
let _cursor = null;
let _nextCursor = null;
let _cursorHistory = [];
let _page = 1;
let _loadGen = 0;
const _navigation = createPaginationNavigationGuard();
let _activeFilters = { status: '', type: '' };
const PAGE_SIZE = 50;

function resetPagination() {
  _navigation.reset();
  _cursor = null;
  _nextCursor = null;
  _cursorHistory = [];
  _page = 1;
}

async function loadContactList({ status = '', type = '' } = {}) {
  const gen = ++_loadGen;
  _activeFilters = { status, type };
  const body = document.getElementById('admin-contact-body');
  const statusEl = document.getElementById('admin-contact-status');
  if (!body) return;

  statusEl.textContent = 'Loading...';
  body.innerHTML = '';

  try {
    const params = new URLSearchParams();
    params.set('limit', PAGE_SIZE);
    if (_cursor) params.set('cursor', _cursor);
    if (status) params.set('status', status);
    if (type) params.set('type', type);
    const qs = params.toString();
    const url = qs ? `/api/admin/contact?${qs}` : '/api/admin/contact';
    const data = await api.get(url);
    if (gen !== _loadGen) return 'stale';
    const rows = data.submissions;
    _nextCursor = data.next_cursor;
    document.getElementById('contact-prev').disabled = _cursorHistory.length === 0;
    document.getElementById('contact-next').disabled = !data.has_more;
    document.getElementById('contact-page-info').textContent = `Page ${_page}`;

    if (!rows.length) {
      statusEl.textContent = 'No submissions found.';
      return true;
    }
    statusEl.textContent = '';

    body.innerHTML = rows.map(r => {
      const excerpt = (r.body_preview || '').replace(/\s+/g, ' ').slice(0, 100)
        + (r.body_preview?.length > 100 ? '...' : '');
      return `
      <tr class="contact-row-clickable" data-id="${esc(r.id)}">
        <td style="white-space:nowrap">${esc(fmtDate(r.created_at))}</td>
        <td style="white-space:nowrap">${typeBadge(r)}</td>
        <td>${esc(r.name)}</td>
        <td>${esc(r.subject)}</td>
        <td class="contact-excerpt">${esc(excerpt)}</td>
        <td>${statusBadge(r.status)}</td>
        <td style="white-space:nowrap">${r.resolved_at ? esc(fmtDate(r.resolved_at)) + (r.resolved_by_username ? ' by ' + esc(r.resolved_by_username) : '') : '–'}</td>
      </tr>`;
    }).join('');

    body.querySelectorAll('.contact-row-clickable').forEach(tr => {
      tr.addEventListener('click', () => {
        navigate(`/admin/contact/${tr.dataset.id}`);
      });
    });
    return true;
  } catch {
    if (gen !== _loadGen) return 'stale';
    statusEl.textContent = 'Failed to load submissions.';
    return false;
  }
}

async function loadContactDetail(id) {
  const content = document.getElementById('admin-contact-detail-content');
  if (!content) return;
  content.innerHTML = '<div class="admin-status">Loading...</div>';

  try {
    const sub = await api.get(`/api/admin/contact/${id}`);
    if (sub.status === 'new') {
      try {
        await api.patch(`/api/admin/contact/${id}`, { status: 'read' });
        sub.status = 'read';
      } catch {
        // Detail remains readable; the explicit mutation can be retried later.
      }
    }

    const screenshots = sub.screenshots || [];
    const screenshotHtml = screenshots.length ? `
      <div class="card admin-card">
        <div class="card-label">SCREENSHOTS</div>
        <div class="contact-detail-screenshots">
          ${screenshots.map(s => `<a href="/api/admin/contact/${esc(id)}/screenshot/${esc(s.name)}" target="_blank"><img src="/api/admin/contact/${esc(id)}/screenshot/${esc(s.name)}" alt="${esc(s.original_name)}" loading="lazy"></a>`).join('')}
        </div>
      </div>
    ` : '';

    const userLink = sub.username
      ? `<div class="contact-detail-meta-row"><span class="contact-detail-meta-label">User</span><a href="/u/${esc(sub.username)}" style="color:var(--accent)">${esc(sub.username)}</a></div>`
      : '';

    const isGameReport = sub.type === 'game_report';
    // Deep-link that boots the main app straight into a solo session for the
    // exact reported game/ROM. Same scheme the library "Play" button uses
    // (`/?system=…&rom=…&solo=1`); opened in a new tab so the report stays put.
    // The server resolves `play_target` from the roms table (served filename +
    // system) so the link works even though the stored `rom_filename` is the
    // inner, un-fetchable name for zipped ROMs. Null when the ROM can't be
    // matched (e.g. user-uploaded ROMs not in the library).
    const playTarget = sub.play_target;
    const playUrl = (isGameReport && playTarget?.system_id && playTarget?.rom_filename)
      ? `/?system=${encodeURIComponent(playTarget.system_id)}&rom=${encodeURIComponent(playTarget.rom_filename)}&solo=1`
      : null;
    const gameContextHtml = isGameReport ? `
      <div class="card admin-card">
        <div class="card-label">GAME REPORT</div>
        <div class="contact-detail-meta">
          <div class="contact-detail-meta-row"><span class="contact-detail-meta-label">Game</span><span>${esc(sub.game_title || '–')}</span></div>
          <div class="contact-detail-meta-row"><span class="contact-detail-meta-label">System</span><span>${esc((sub.system_id || '').toUpperCase() || '–')}</span></div>
          <div class="contact-detail-meta-row"><span class="contact-detail-meta-label">ROM file</span><span>${esc(sub.rom_filename || '–')}</span></div>
          <div class="contact-detail-meta-row"><span class="contact-detail-meta-label">Reason</span><span>${esc(GAME_REPORT_REASON_LABELS[sub.reason] || sub.reason || '–')}</span></div>
          ${sub.game_id ? `<div class="contact-detail-meta-row"><span class="contact-detail-meta-label">Game ID</span><span>${esc(String(sub.game_id))}</span></div>` : ''}
        </div>
        ${playUrl ? `<a class="btn btn-mode btn-primary" href="${esc(playUrl)}" target="_blank" rel="noopener" style="margin-top:10px;display:inline-block">▶  Play in solo mode (new window)</a>` : ''}
      </div>
    ` : '';

    const resolutionHtml = isGameReport ? `
          <textarea id="contact-detail-resolution" class="settings-input" rows="2" placeholder="Resolution message, sent to the reporter as a notification (optional)" style="width:100%;box-sizing:border-box;margin-top:8px">${esc(sub.resolution_message || '')}</textarea>
    ` : '';

    const removeHtml = isGameReport ? `
      <div class="card admin-card">
        <div class="card-label">DANGER ZONE</div>
        <button class="btn btn-mode" id="contact-remove-game" style="background:#b3261e;color:#fff">Remove from library</button>
        <div id="contact-remove-confirm" hidden style="margin-top:10px">
          <p style="margin:0 0 8px">This hides <strong>${esc(sub.game_title || 'this game')}</strong> from the library and makes it unplayable everywhere. Are you sure?</p>
          <button class="btn btn-mode" id="contact-remove-yes" style="background:#b3261e;color:#fff">Yes, remove it</button>
          <button class="btn btn-mode" id="contact-remove-cancel">Cancel</button>
          <span id="contact-remove-status" class="admin-status" style="margin:0 0 0 8px"></span>
        </div>
      </div>
    ` : '';

    content.innerHTML = `
      <div class="card admin-card">
        <div class="contact-detail-meta">
          <div class="contact-detail-meta-row"><span class="contact-detail-meta-label">From</span><span>${esc(sub.name)} &lt;${esc(sub.email)}&gt;</span></div>
          <div class="contact-detail-meta-row"><span class="contact-detail-meta-label">Date</span><span>${esc(fmtDate(sub.created_at))}</span></div>
          <div class="contact-detail-meta-row"><span class="contact-detail-meta-label">Status</span>${statusBadge(sub.status)}${sub.resolved_at ? ' · ' + esc(fmtDate(sub.resolved_at)) + (sub.resolved_by_username ? ' by ' + esc(sub.resolved_by_username) : '') : ''}</div>
          ${userLink}
        </div>
        <div class="contact-detail-subject">${esc(sub.subject)}</div>
        <div class="contact-detail-body">${esc(sub.body)}</div>
      </div>
      ${gameContextHtml}
      ${screenshotHtml}
      <div class="card admin-card">
        <div class="card-label">UPDATE STATUS</div>
        <div class="contact-detail-status-form">
          <select class="settings-input settings-select" id="contact-detail-status" style="max-width:160px">
            <option value="new" ${sub.status === 'new' ? 'selected' : ''}>New</option>
            <option value="read" ${sub.status === 'read' ? 'selected' : ''}>Read</option>
            <option value="resolved" ${sub.status === 'resolved' ? 'selected' : ''}>Resolved</option>
          </select>
          <button class="btn btn-mode btn-primary" id="contact-detail-save">Update</button>
          <span id="contact-detail-save-status" class="admin-status" style="margin:0"></span>
        </div>
        ${resolutionHtml}
      </div>
      ${removeHtml}
    `;

    document.getElementById('contact-detail-save')?.addEventListener('click', async () => {
      const statusSelect = document.getElementById('contact-detail-status');
      const saveStatus = document.getElementById('contact-detail-save-status');
      const resolutionEl = document.getElementById('contact-detail-resolution');
      const newStatus = statusSelect.value;
      const payload = { status: newStatus };
      if (resolutionEl) payload.resolution_message = resolutionEl.value;
      try {
        await api.patch(`/api/admin/contact/${id}`, payload);
        saveStatus.textContent = (newStatus === 'resolved' && isGameReport && resolutionEl?.value.trim())
          ? 'Saved, reporter notified'
          : 'Saved';
        saveStatus.style.color = 'var(--accent)';
        sub.status = newStatus;
      } catch {
        saveStatus.textContent = 'Failed';
        saveStatus.style.color = '#f06060';
      }
      setTimeout(() => { saveStatus.textContent = ''; }, 3000);
    });

    // ── Remove from library: two clicks (button → confirm) ──
    const removeBtn = document.getElementById('contact-remove-game');
    const confirmBox = document.getElementById('contact-remove-confirm');
    removeBtn?.addEventListener('click', () => {
      confirmBox.hidden = false;
      removeBtn.hidden = true;
    });
    document.getElementById('contact-remove-cancel')?.addEventListener('click', () => {
      confirmBox.hidden = true;
      removeBtn.hidden = false;
    });
    document.getElementById('contact-remove-yes')?.addEventListener('click', async () => {
      const st = document.getElementById('contact-remove-status');
      st.textContent = 'Removing…';
      st.style.color = '';
      try {
        await api.post(`/api/admin/contact/${id}/remove-game`, {});
        st.textContent = 'Removed from library';
        st.style.color = 'var(--accent)';
        setTimeout(() => loadContactDetail(id), 900);
      } catch {
        st.textContent = 'Failed';
        st.style.color = '#f06060';
      }
    });
  } catch {
    content.innerHTML = '<div class="admin-status">Failed to load submission.</div>';
  }
}

// ── Exports ──────────────────────────────────────────────

export function renderList() {
  const filters = document.querySelectorAll('[data-screen="contact"] .contact-filter-btn');
  if (!_listBound) {
    _listBound = true;
    filters.forEach(btn => {
      btn.addEventListener('click', () => {
        filters.forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        resetPagination();
        loadContactList({ status: btn.dataset.status || '', type: btn.dataset.type || '' });
      });
    });
    document.getElementById('contact-prev')?.addEventListener('click', async () => {
      if (!_cursorHistory.length) return;
      await _navigation.run(async () => {
        const snap = { cursor: _cursor, page: _page, history: [..._cursorHistory] };
        _cursor = _cursorHistory.pop() || null;
        _page--;
        if (!await loadContactList(_activeFilters)) {
          _cursor = snap.cursor; _page = snap.page; _cursorHistory = snap.history;
        }
      });
    });
    document.getElementById('contact-next')?.addEventListener('click', async () => {
      if (!_nextCursor) return;
      await _navigation.run(async () => {
        const snap = { cursor: _cursor, page: _page, history: [..._cursorHistory] };
        _cursorHistory.push(_cursor);
        _cursor = _nextCursor;
        _page++;
        if (!await loadContactList(_activeFilters)) {
          _cursor = snap.cursor; _page = snap.page; _cursorHistory = snap.history;
        }
      });
    });
  }
  // Load from whichever filter is active in markup (defaults to "Open",
  // which hides resolved submissions and game reports).
  const active = document.querySelector('[data-screen="contact"] .contact-filter-btn.active');
  resetPagination();
  loadContactList({ status: active?.dataset.status || '', type: active?.dataset.type || '' });
}

export function renderDetail(params) {
  loadContactDetail(params.id);
}
